/**
 * Shared district display helpers.
 *
 * Canonical format (ratified 2026-08-27): jurisdiction prefix + chamber code +
 * hyphen + two-digit number — 'CO HD-07', 'CO SD-07', 'US HD-07'. A bare
 * district number renders as '07'. Stored/incoming data is never rewritten;
 * normalization happens at presentation.
 *
 * Mirrors the server: electoral_districts.display_label (migration
 * 1788000000000) and backend/src/sql/candidateDistrictDisplaySql.js. Change
 * the three together or filter round-trips break.
 *
 * Canonical sort: CO House numeric, then CO Senate numeric, then US House
 * numeric, then anything else — see districtSortKey / compareDistricts.
 */

/** '7' / 7 / '07' → '07'. Non-numeric input passes through untouched. */
export function padDistrictNumber(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = String(value).trim();
  return /^\d+$/.test(s) ? String(parseInt(s, 10)).padStart(2, '0') : s;
}

/**
 * Full district name. Prefers a server-provided display label; otherwise
 * derives the canonical label from a raw value + chamber. State chambers only —
 * US House labels come from the server (candidates have no chamber field here).
 */
export function formatDistrict(
  districtDisplay?: string | null,
  rawDistrict?: string | null,
  chamber?: string | null
): string {
  if (districtDisplay) return districtDisplay;
  if (rawDistrict === null || rawDistrict === undefined) return '';
  const trimmed = String(rawDistrict).trim();
  if (!trimmed) return '';
  if (/^\d+$/.test(trimmed)) {
    const padded = padDistrictNumber(trimmed);
    const c = (chamber || '').toLowerCase();
    if (c.includes('senate')) return `CO SD-${padded}`;
    if (c.includes('house')) return `CO HD-${padded}`;
    return padded;
  }
  return trimmed;
}

/**
 * Lexicographically sortable key implementing the canonical order.
 * Understands the canonical labels, the retired 'House District 7' /
 * 'Senate District 7' / 'US HD 7' forms, geocoder shapes ('HD-8', 'SD-17',
 * 'CO-2'), and bare numbers (ranked as CO House, the most common bare case
 * has its group supplied by the column it sits in — callers with mixed bare
 * numbers should format first).
 */
export function districtSortKey(value: string | number | null | undefined): string {
  const s = String(value ?? '').trim();
  if (!s) return '9-';
  const upper = s.toUpperCase();
  const num = (m: RegExpMatchArray | null) =>
    m ? String(parseInt(m[1], 10)).padStart(3, '0') : '999';

  if (/^CO HD-|^HOUSE DISTRICT |^HD[-\s]/.test(upper)) return `0-${num(upper.match(/(\d+)/))}`;
  if (/^CO SD-|^SENATE DISTRICT |^SD[-\s]/.test(upper)) return `1-${num(upper.match(/(\d+)/))}`;
  if (/^US HD[-\s]|^CO-\d/.test(upper)) return `2-${num(upper.match(/(\d+)/))}`;
  if (/^\d+$/.test(upper)) return `0-${num(upper.match(/(\d+)/))}`;
  return `9-${upper}`;
}

/**
 * Presentation-normalize a district value in whatever shape it was stored:
 * geocoder ('HD-8' / 'SD-17' / 'CO-2'), share-scope keys ('House-7'), legacy
 * labels ('House District 7'), canonical labels (number padded), bare numbers.
 * Anything unrecognized returns unchanged — stored data is never a casualty.
 */
export function normalizeDistrictLabel(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (/^\d+$/.test(s)) return padDistrictNumber(s);

  let m = /^(CO|US)\s+(HD|SD)-(\d+)$/i.exec(s);
  if (m) return `${m[1].toUpperCase()} ${m[2].toUpperCase()}-${padDistrictNumber(m[3])}`;

  m = /^(HD|SD)[-\s](\d+)$/i.exec(s);
  if (m) return `CO ${m[1].toUpperCase()}-${padDistrictNumber(m[2])}`;

  m = /^CO-(\d+)$/i.exec(s);
  if (m) return `US HD-${padDistrictNumber(m[1])}`;

  m = /^(House|Senate)\s+District\s+(\d+)$/i.exec(s);
  if (m) return `CO ${m[1].toLowerCase() === 'senate' ? 'SD' : 'HD'}-${padDistrictNumber(m[2])}`;

  m = /^US\s+HD\s+(\d+)$/i.exec(s);
  if (m) return `US HD-${padDistrictNumber(m[1])}`;

  m = /^(House|Senate)-(\d+)$/i.exec(s);
  if (m) return `CO ${m[1].toLowerCase() === 'senate' ? 'SD' : 'HD'}-${padDistrictNumber(m[2])}`;

  return s;
}

/** Comparator for the canonical district order (CO House → CO Senate → US House). */
export function compareDistricts(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number {
  return districtSortKey(a).localeCompare(districtSortKey(b));
}
